// Contratos públicos de Hilo (docs/01-contratos.md). Tipos compartidos por API y panel.
// Los esquemas Zod rechazan campos desconocidos (strictObject).
import { z } from 'zod';

export const CHANNELS = ['WHATSAPP', 'MESSENGER', 'INSTAGRAM'] as const;
export const ROLES = ['ADMIN', 'SUPERVISOR', 'AGENT'] as const;
export const CONVERSATION_STATES = ['OPEN', 'WAITING', 'RESOLVED'] as const;
export const MESSAGE_STATUSES = ['QUEUED', 'SENDING', 'ACCEPTED', 'DELIVERED', 'READ', 'FAILED', 'UNKNOWN'] as const;
export const MESSAGE_KINDS = ['TEXT', 'IMAGE', 'AUDIO', 'VIDEO', 'DOCUMENT', 'UNSUPPORTED'] as const;
export const REFERRAL_KINDS = ['AD', 'POST', 'STORY', 'UNKNOWN'] as const;
/** Correcciones 01b (M1): estado de revisión de identidad del contacto de una conversación. */
export const CONTACT_IDENTITY_STATUSES = ['VERIFIED', 'PENDING_REVIEW'] as const;

export type Channel = (typeof CHANNELS)[number];
export type Role = (typeof ROLES)[number];
export type ConversationState = (typeof CONVERSATION_STATES)[number];
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];
export type MessageKind = (typeof MESSAGE_KINDS)[number];
export type ContactIdentityStatus = (typeof CONTACT_IDENTITY_STATUSES)[number];

export interface Actor {
  userId: string;
  role: Role;
  sessionId: string;
}

export interface Referral {
  kind: (typeof REFERRAL_KINDS)[number];
  externalId: string | null;
  url: string | null;
  title: string | null;
  previewAttachmentId: string | null;
}

export interface ConversationView {
  id: string;
  connectionId: string;
  contactId: string;
  state: ConversationState;
  assigneeId: string | null;
  revision: number;
  lastActivityAt: string;
  lastInboundAt: string | null;
  connectionName: string;
  channel: Channel;
  /** Correcciones 01b: estado de revisión de identidad del contacto de esta conversación. */
  identityStatus: ContactIdentityStatus;
  /**
   * Motivo cuando `identityStatus` es `PENDING_REVIEW`; `null` si no aplica. El esquema Prisma
   * (`ContactIdentity.identityReviewReason`) lo tipa como `String?` libre, no como enum: hoy solo
   * existe `'RETIRED_PHONE'`, pero el propio comentario del esquema anticipa más motivos futuros, así
   * que el contrato usa `string | null` para no perder cadenas nuevas si el backend agrega otras.
   */
  identityReviewReason: string | null;
  /** Bandeja web: nombre de perfil del contacto; `null` si el canal no lo dio. Nunca el teléfono. */
  contactDisplayName: string | null;
  /** Ruta privada del mismo origen; nunca URL ni credenciales del proveedor. */
  contactPhotoUrl?: string | null;
  /** Bandeja web: nombre visible del responsable; `null` si está libre. */
  assigneeDisplayName: string | null;
  /** Bandeja web: último mensaje por orden de recepción; `null` si la conversación no tiene mensajes. */
  lastMessage: ConversationLastMessage | null;
}

export interface ConversationLastMessage {
  direction: 'INBOUND' | 'OUTBOUND';
  kind: MessageKind;
  /** Texto recortado a CONVERSATION_PREVIEW_LENGTH caracteres (más «…» si se recortó); `null` sin texto. */
  preview: string | null;
  status: MessageStatus;
  occurredAt: string;
}

export interface SendInput {
  clientRequestId: string;
  text: string;
  attachmentIds: string[];
  template?: { name: string; language: string; parameters: string[] };
}

/**
 * Adjunto tal como lo ve el panel (tarea 7). Nunca lleva URL de almacenamiento: el contenido se pide a
 * GET /api/v1/attachments/:id/content, que comprueba el acceso en cada descarga.
 */
export interface AttachmentView {
  id: string;
  kind: Exclude<MessageKind, 'TEXT' | 'UNSUPPORTED'>;
  mime: string;
  /** Desconocido (null) mientras un medio del proveedor no se ha descargado. */
  bytes: number | null;
  name: string | null;
  state: 'PENDING' | 'READY' | 'FAILED';
}

export interface MessageView {
  id: string;
  conversationId: string;
  direction: 'INBOUND' | 'OUTBOUND';
  kind: MessageKind;
  text: string | null;
  attachmentIds: string[];
  /** Bandeja web: los adjuntos del mensaje, en el mismo orden que attachmentIds. */
  attachments: AttachmentView[];
  authorUserId: string | null;
  providerMessageId: string | null;
  status: MessageStatus;
  occurredAt: string;
  receivedAt: string;
  referral: Referral | null;
  errorCode: string | null;
  /** Bandeja web: nombre visible del autor; `null` en entrantes o si no hay autor. */
  authorDisplayName: string | null;
}

export interface SendPolicy {
  allowed: boolean;
  reason: string | null;
  validUntil: string | null;
  /** Aditivo: límite aplicable a una respuesta manual de Messenger/Instagram. */
  replyWindow?: 'STANDARD' | 'HUMAN_AGENT';
  allowedKinds: MessageKind[];
  maxBytesByKind: Partial<Record<MessageKind, number>>;
  templates: Array<{ name: string; language: string }>;
}

export interface ApiError {
  code: string;
  message: string;
  requestId: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** Usuario tal como lo ve el panel. Nunca incluye hashes ni contraseñas. */
export interface UserView {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  active: boolean;
  mustChangePassword: boolean;
  timezone: string;
}

export interface SessionView {
  user: UserView;
  csrfToken: string;
  mustChangePassword: boolean;
  /** Operador revisor de Meta: la web oculta lo que no usa (el tablero). Ausente equivale a false. */
  metaReviewer?: boolean;
}

/** Respuesta de creación/restablecimiento: la contraseña temporal se presenta una sola vez. */
export interface TemporaryPasswordView {
  user: UserView;
  temporaryPassword: string;
  expiresAt: string;
}

/** Conexión visible para el panel; sin credenciales ni referencias a ellas. */
export interface ConnectionView {
  id: string;
  channel: Channel;
  displayName: string;
  enabled: boolean;
  mode: 'TEST' | 'PRODUCTION';
  lastWebhookAt: string | null;
}

// ---------------------------------------------------------------------------
// Entrega F · alta de cuentas de Meta y solicitudes de eliminación (solo ADMIN salvo el estado público)
// ---------------------------------------------------------------------------

/** Conexión con los datos de gestión que ve un ADMIN en Conexiones. Sin credenciales ni sus referencias. */
export interface AdminConnectionView extends ConnectionView {
  /** CONSENT = alta con Facebook Login for Business o Instagram Login; CONFIGURATION = variables del servidor. */
  origin: 'CONSENT' | 'CONFIGURATION';
  /** Solo Instagram: modalidad de su token. null si no hay credencial. */
  instagramLogin: 'FACEBOOK_LOGIN' | 'INSTAGRAM_LOGIN' | null;
  credentialsPresent: boolean;
  /** Instagram consent tokens only; no token values or references. */
  tokenExpiresAt?: string | null;
  tokenRefreshFailure?: string | null;
  tokenRefreshAttempts?: number;
  tokenRefreshNextAt?: string | null;
  grantedAt: string | null;
  grantedBy: { id: string; displayName: string } | null;
  accessUserIds: string[];
}

export type WhatsAppOnboardingConfigView = { enabled: false } | {
  enabled: true; appId: string; configId: string; graphVersion: string;
};
export interface WhatsAppOnboardingStartView { state: string; expiresAt: string }
export interface WhatsAppOnboardingView {
  id: string;
  status: 'STARTED' | 'EXCHANGING' | 'AUTHORIZED' | 'COMPLETED' | 'FAILED';
  expiresAt: string; failureCode: string | null;
  numbers: Array<{ phoneNumberId: string; wabaId: string; name: string; displayPhoneNumber: string }>;
}
export interface MetaOnboardingStartView {
  /** Diálogo oficial de Meta; el navegador solo lleva el state opaco. */
  authorizeUrl: string;
  expiresAt: string;
}

export interface MetaOnboardingPageView {
  pageId: string;
  name: string;
  /** La persona tiene la tarea MESSAGING sobre la página. */
  canMessage: boolean;
  /** Tiene MANAGE, MODERATE o CREATE_CONTENT, que exige la suscripción de la página. */
  canSubscribe: boolean;
  instagram: 'AVAILABLE' | 'NOT_LINKED' | 'NOT_AUTHORIZED';
  /** Existe una conexión Messenger por consentimiento; puede necesitar reconexión. */
  connected: boolean;
  /** Messenger habilitado con credencial vigente: no se puede elegir otra vez. */
  active: boolean;
  /** La página o su Instagram ya tienen otra configuración: Hilo no la convierte. */
  blocked: boolean;
}

export interface MetaOnboardingView {
  id: string;
  status: 'IN_PROGRESS' | 'AUTHORIZED' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  expiresAt: string;
  failureCode: string | null;
  pages: MetaOnboardingPageView[];
}

export interface ConnectPageResultView {
  connections: ConnectionView[];
}

export const DATA_DELETION_STATUSES = ['IN_PROGRESS', 'COMPLETED', 'RETAINED'] as const;
export type DataDeletionStatus = (typeof DATA_DELETION_STATUSES)[number];

/** Estado público de una solicitud de eliminación (página /eliminacion-datos): sin datos personales. */
export interface DataDeletionStatusView {
  confirmationCode: string;
  status: DataDeletionStatus;
  publicNote: string | null;
  receivedAt: string;
  updatedAt: string;
}

export interface DataDeletionRequestView extends DataDeletionStatusView {
  id: string;
  responsible: { id: string; displayName: string } | null;
}

// ---------------------------------------------------------------------------
// Tablero del equipo · tareas internas (trabajo creativo, publicidad, entregas)
// ---------------------------------------------------------------------------
export const WORK_ITEM_STATUSES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE'] as const;
export type WorkItemStatus = (typeof WORK_ITEM_STATUSES)[number];
export const WORK_ITEM_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;
export type WorkItemPriority = (typeof WORK_ITEM_PRIORITIES)[number];
export const WORK_ITEM_FOCUSES = ['overdue', 'dueWeek', 'publishWeek'] as const;
export type WorkItemFocus = (typeof WORK_ITEM_FOCUSES)[number];

export interface UserRef {
  id: string;
  displayName: string;
}

/**
 * Tarea del tablero. Las fechas de entrega y publicación son días de calendario `YYYY-MM-DD` (sin hora ni zona).
 * La ve todo el equipo; `revision` sube con cada cambio (control de concurrencia como en conversaciones).
 */
export interface WorkItemView {
  id: string;
  title: string;
  /** Texto libre; cadena vacía si no hay descripción. */
  description: string;
  status: WorkItemStatus;
  priority: WorkItemPriority;
  /** Proyecto o cliente libre, p. ej. «La Castellana». */
  project: string | null;
  assignee: UserRef | null;
  createdBy: UserRef;
  dueDate: string | null;
  publishDate: string | null;
  /** Instante en que pasó a DONE; null si no está completada. */
  completedAt: string | null;
  commentCount: number;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkItemCommentView {
  id: string;
  workItemId: string;
  author: UserRef;
  body: string;
  createdAt: string;
}

export interface WorkItemDetailView extends WorkItemView {
  /** Del más antiguo al más reciente. */
  comments: WorkItemCommentView[];
}

export interface WorkItemWorkloadEntry {
  id: string;
  displayName: string;
  /** Tareas no completadas asignadas a esa persona (con los filtros del resumen). */
  open: number;
}

/** Contadores del tablero calculados en el servidor. «Hoy» es el día en la zona horaria del usuario. */
export interface WorkItemSummaryView {
  /** Con los mismos filtros; DONE respeta completedSince. */
  byStatus: Record<WorkItemStatus, number>;
  /** No completadas con dueDate < today. */
  overdue: number;
  /** No completadas con dueDate entre today y today+6. */
  dueThisWeek: number;
  /** No completadas con publishDate entre today y today+6. */
  publishThisWeek: number;
  /** Personas con al menos una tarea abierta, de más a menos (empate: displayName, id). */
  workload: WorkItemWorkloadEntry[];
  /** YYYY-MM-DD */
  today: string;
}

/** Aviso Socket.IO (fase 2): sin texto, teléfono ni medios. */
export interface HiloChanged {
  /** `workItem`: cambió una tarea del tablero del equipo (se avisa a todo el equipo conectado). */
  kind: 'conversation' | 'notification' | 'access' | 'workItem';
  id: string;
  revision: number;
}

// ---------------------------------------------------------------------------
// Límites compartidos
// ---------------------------------------------------------------------------
export const MAX_TEXT_LENGTH = 4000;
export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;
export const PAGE_LIMIT_DEFAULT = 30;
export const PAGE_LIMIT_MAX = 100;
export const CONVERSATION_PREVIEW_LENGTH = 140;
export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_MAX_LENGTH = 100;
export const WORK_ITEM_TITLE_MAX = 200;
export const WORK_ITEM_DESCRIPTION_MAX = 10000;
export const WORK_ITEM_PROJECT_MAX = 80;
export const WORK_ITEM_COMMENT_MAX = 4000;
export const WORK_ITEM_LIST_MAX = 500;

// ---------------------------------------------------------------------------
// Esquemas de entrada (validación API)
// ---------------------------------------------------------------------------
const uuid = z.uuid();
const password = z.string().min(1).max(PASSWORD_MAX_LENGTH);
const newPassword = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
// Entero de 32 bits como la columna revision de PostgreSQL.
const revision = z.number().int().min(1).max(2_147_483_647);

export function isValidTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}
const timeZone = z.string().min(1).max(64).refine(isValidTimeZone, 'Zona horaria inválida');

export const LoginInputSchema = z.strictObject({
  email: z.string().min(3).max(254),
  password,
});

export const ChangePasswordInputSchema = z.strictObject({
  currentPassword: password,
  newPassword,
});

export const ReauthenticateInputSchema = z.strictObject({ password });

export const CreateUserInputSchema = z.strictObject({
  email: z.email().max(254),
  displayName: z.string().trim().min(1).max(100),
  role: z.enum(ROLES),
});

export const UpdateUserInputSchema = z
  .strictObject({
    active: z.boolean().optional(),
    displayName: z.string().trim().min(1).max(100).optional(),
    role: z.enum(ROLES).optional(),
    timezone: timeZone.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Sin cambios');

export const ConnectionAccessInputSchema = z.strictObject({
  userIds: z.array(uuid).max(500),
});

/** Pruebas ↔ modo real. Solo ADMIN; PRODUCTION responde a cualquier cliente, no solo a la lista de pruebas. */
export const ConnectionModeInputSchema = z.strictObject({
  mode: z.enum(['TEST', 'PRODUCTION']),
});

export const WhatsAppAuthorizeInputSchema = z.strictObject({
  state: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  code: z.string().regex(/^[\x21-\x7e]{1,2048}$/),
  wabaId: z.string().regex(/^[0-9]{1,32}$/),
  phoneNumberId: z.string().regex(/^[0-9]{1,32}$/).optional(),
});
export const ConnectWhatsAppInputSchema = z.strictObject({ phoneNumberId: z.string().regex(/^[0-9]{1,32}$/) });

export const ConnectPageInputSchema = z.strictObject({
  pageId: z.string().regex(/^[0-9]{1,32}$/),
});

export const DATA_DELETION_NOTE_MAX = 1000;

export const DataDeletionUpdateInputSchema = z
  .strictObject({
    responsibleId: uuid.nullable().optional(),
    status: z.enum(DATA_DELETION_STATUSES).optional(),
    publicNote: z.string().trim().max(DATA_DELETION_NOTE_MAX).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Sin cambios');

export const ClaimInputSchema = z.strictObject({ expectedRevision: revision });

export const AssignmentInputSchema = z.strictObject({
  assigneeId: uuid.nullable(),
  expectedRevision: revision,
});

export const StateChangeInputSchema = z.strictObject({
  state: z.enum(CONVERSATION_STATES),
  expectedRevision: revision,
});

const limit = z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT);
const cursor = z.string().min(1).max(512).optional();

export const ListConversationsQuerySchema = z.strictObject({
  connectionId: uuid.optional(),
  state: z.enum(CONVERSATION_STATES).optional(),
  assigned: z.enum(['me', 'free', 'all']).default('all'),
  /** Búsqueda por nombre de contacto, sin distinguir mayúsculas. */
  q: z.string().trim().min(SEARCH_MIN_LENGTH).max(SEARCH_MAX_LENGTH).optional(),
  cursor,
  limit,
});

export const ListMessagesQuerySchema = z.strictObject({ cursor, limit });

export const SendInputSchema = z
  .strictObject({
    clientRequestId: uuid,
    text: z.string().max(MAX_TEXT_LENGTH),
    attachmentIds: z.array(uuid).max(MAX_ATTACHMENTS),
    template: z
      .strictObject({
        name: z.string().min(1).max(512),
        language: z.string().min(2).max(16),
        parameters: z.array(z.string().max(1024)).max(20),
      })
      .optional(),
  })
  .superRefine((input, ctx) => {
    const hasText = input.text.trim().length > 0;
    const hasAttachments = input.attachmentIds.length > 0;
    if (input.template) {
      if (hasText || hasAttachments) {
        ctx.addIssue({ code: 'custom', message: 'La plantilla no se combina con texto ni adjuntos' });
      }
    } else if (!hasText && !hasAttachments) {
      ctx.addIssue({ code: 'custom', message: 'El envío necesita texto, adjuntos o plantilla' });
    }
    if (new Set(input.attachmentIds).size !== input.attachmentIds.length) {
      ctx.addIssue({ code: 'custom', message: 'Adjuntos repetidos' });
    }
  });

// Día de calendario real (rechaza 2026-02-30).
const calendarDate = z.iso.date();

export const CreateWorkItemInputSchema = z.strictObject({
  title: z.string().trim().min(1).max(WORK_ITEM_TITLE_MAX),
  description: z.string().max(WORK_ITEM_DESCRIPTION_MAX).default(''),
  status: z.enum(WORK_ITEM_STATUSES).default('TODO'),
  priority: z.enum(WORK_ITEM_PRIORITIES).default('NORMAL'),
  project: z.string().trim().min(1).max(WORK_ITEM_PROJECT_MAX).nullable().default(null),
  assigneeId: uuid.nullable().default(null),
  dueDate: calendarDate.nullable().default(null),
  publishDate: calendarDate.nullable().default(null),
});

export const UpdateWorkItemInputSchema = z
  .strictObject({
    expectedRevision: revision,
    title: z.string().trim().min(1).max(WORK_ITEM_TITLE_MAX).optional(),
    description: z.string().max(WORK_ITEM_DESCRIPTION_MAX).optional(),
    status: z.enum(WORK_ITEM_STATUSES).optional(),
    priority: z.enum(WORK_ITEM_PRIORITIES).optional(),
    project: z.string().trim().min(1).max(WORK_ITEM_PROJECT_MAX).nullable().optional(),
    assigneeId: uuid.nullable().optional(),
    dueDate: calendarDate.nullable().optional(),
    publishDate: calendarDate.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 1, 'Sin cambios');

export const ListWorkItemsQuerySchema = z.strictObject({
  status: z.enum(WORK_ITEM_STATUSES).optional(),
  /** UUID de una persona, `me` (yo) o `none` (sin asignar). */
  assignee: z.union([uuid, z.enum(['me', 'none'])]).optional(),
  project: z.string().trim().min(1).max(WORK_ITEM_PROJECT_MAX).optional(),
  /** Busca en título y proyecto, sin distinguir mayúsculas. */
  q: z.string().trim().min(SEARCH_MIN_LENGTH).max(SEARCH_MAX_LENGTH).optional(),
});

export const CreateWorkItemCommentInputSchema = z.strictObject({
  body: z.string().trim().min(1).max(WORK_ITEM_COMMENT_MAX),
});

const workItemFilters = {
  /** UUID de una persona, `me` (yo) o `none` (sin asignar). */
  assignee: z.union([uuid, z.enum(['me', 'none'])]).optional(),
  project: z.string().trim().min(1).max(WORK_ITEM_PROJECT_MAX).optional(),
  q: z.string().trim().min(SEARCH_MIN_LENGTH).max(SEARCH_MAX_LENGTH).optional(),
  /** Solo afecta a DONE: completedAt desde el inicio de ese día en la zona del usuario. */
  completedSince: calendarDate.optional(),
  /**
   * Indicadores del pulso (siempre excluyen DONE; «hoy» en la zona del usuario):
   * overdue = dueDate < hoy; dueWeek = dueDate entre hoy y hoy+6; publishWeek = publishDate entre hoy y hoy+6.
   * En el resumen afecta a byStatus y workload, no a los contadores overdue/dueThisWeek/publishThisWeek.
   */
  focus: z.enum(WORK_ITEM_FOCUSES).optional(),
};

/**
 * Una columna del tablero, paginada con cursor opaco. Orden: TODO/IN_PROGRESS/IN_REVIEW por dueDate asc
 * (sin fecha al final) e id; DONE por completedAt desc e id.
 */
export const ListWorkItemsPageQuerySchema = z.strictObject({
  status: z.enum(WORK_ITEM_STATUSES),
  ...workItemFilters,
  cursor,
  limit,
});

export const WorkItemSummaryQuerySchema = z.strictObject(workItemFilters);
